import { useEffect, useMemo, useState, useRef, forwardRef, useImperativeHandle } from "react";
import { Printer, Loader2, Sparkles, ImagePlus, Quote, BookOpen, ScrollText, NotebookPen, Pencil, Trash2, Check, X, ChevronLeft, ChevronRight } from "lucide-react";
import { ScrollToTop } from "../components/ScrollToTop";
import { toast } from "sonner";
import { api } from "../lib/api";
import { fileToResizedDataUrl } from "../lib/imageUtils";
import { useAuth } from "../context/AuthContext";

const MONTHS_NL = ["", "Januari", "Februari", "Maart", "April", "Mei", "Juni", "Juli", "Augustus", "September", "Oktober", "November", "December"];
const COVER_PHOTO = "https://static.prod-images.emergentagent.com/jobs/6947acc3-96d8-4494-b4a1-a5455aa06179/images/d5af2ed8129ee12377ee3306c56ab5afb70e6d67d5ca58b8a0b5c0be9d4287b7.jpeg";

const highlightInParagraph = (text, quote) => {
  if (!quote) return { node: text, matched: false };
  const idx = text.toLowerCase().indexOf(quote.toLowerCase());
  if (idx === -1) return { node: text, matched: false };
  const before = text.slice(0, idx);
  const match = text.slice(idx, idx + quote.length);
  const after = text.slice(idx + quote.length);
  return {
    node: (
      <>
        {before}
        <mark className="rounded px-0.5 font-medium text-[var(--ink)] [box-decoration-break:clone]" style={{ backgroundColor: "color-mix(in srgb, var(--brand) 22%, transparent)" }} data-testid="book-quote-mark">{match}</mark>
        {after}
      </>
    ),
    matched: true,
  };
};
const PAGE_MARK_RE = /\s*<<<PAGINA>>>\s*/g;
const stripMarkers = (t) => (t || "").replace(/[⟦⟧]/g, "").replace(PAGE_MARK_RE, "\n\n");

function ymKey(l) {
  const m = (l.date_iso || "").match(/^(\d{4})-(\d{2})/);
  return m ? `${m[1]}-${m[2]}` : "0000-00";
}
function dayKey(l) {
  const m = (l.date_iso || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? Number(m[3]) : 0;
}
function paragraphs(text) {
  return stripMarkers(text).split(/\n{2,}/).map((p) => p.replace(/\n/g, " ").trim()).filter(Boolean);
}
// Eerste alinea die alleen een datum/plaats-kopje is (bv. "Bussum, 19 Oct. '53.",
// "Amsterdam, 18 November 1953" of "7/ 7 '53") weglaten: die info staat al in de metaregel.
const MONTH_RE = /(januari|februari|febr|maart|mrt|april|mei|juni|juli|augustus|september|sept|oktober|okt|oct|november|december|jan|feb|apr|jun|jul|aug|sep|nov|dec)/i;
const looksLikeDateline = (p) => {
  if (!p) return false;
  const t = p.trim();
  if (t.length > 45) return false;
  if (MONTH_RE.test(t) && /\d/.test(t)) return true;
  if (/\d{1,2}\s*[/\-.]\s*\d{1,2}/.test(t)) return true; // numeriek datum: 7/7, 27-1-'54 ...
  return false;
};
// Aanhef ("Beste Pieter,"), slotgroet ("Hartelijke groeten Ineke", "Uw zoon, Hendrik")
// en adresregels van een ansichtkaart ("Mej. Ineke Koning, ...").
const SALUTATION_RE = /^(beste|lief|lieve|liefste|lieveling|allerliefst|allerliefste|waarde|dag|hallo|hoi|mijn lieve|mijn beste|geachte)\b/i;
const CLOSING_CUE = /((hartelijke|vele|veel|heel veel|innige|lieve|hele|nogmaals|allerhartelijkste)\s+)?groet(en|je|jes)?\b|liefs\b|(dikke |vele |een )?(kus|kusje|kusjes)\b|tot ziens|tot gauw|tot spoedig|hoogachtend|je liefhebbende|(je|uw) (dienstw|dienstwillige|zoon|dochter|toegenegen)/i;
const ADDRESS_RE = /^(mej\.?|mejuffrouw|juffrouw|mevr\.?|mevrouw|mw\.?|dhr\.?|de hr\.?|den hr\.?|\bhr\.?|de heer|den heer|de weled|weled\.?|weledele|weledgeb|weledelgeb|weledgeboren|edelachtb|edele|de edele|fam\.?|familie|t\.a\.v)/i;

// Prefix (aanhef) tot de eerste komma/punt als cursief stuk teruggeven.
const splitSalutation = (text) => {
  if (!SALUTATION_RE.test(text)) return null;
  const m = text.match(/[,.!]/);
  const end = m ? m.index : -1;
  if (end === -1 || end > 45) return null;
  return [text.slice(0, end + 1), text.slice(end + 1)];
};
// Laatste slotgroet-zin binnen een alinea cursief teruggeven.
const splitClosing = (text) => {
  const re = new RegExp(CLOSING_CUE.source, "gi");
  let m, last = -1;
  while ((m = re.exec(text)) !== null) {
    if (m.index >= text.length * 0.45) last = m.index;
    if (m.index === re.lastIndex) re.lastIndex++;
  }
  if (last === -1) return null;
  const before = text.slice(0, last);
  const b = Math.max(before.lastIndexOf(". "), before.lastIndexOf("! "), before.lastIndexOf("? "), before.lastIndexOf("; "));
  const start = b === -1 ? last : b + 2;
  if (start === 0 && text.length > 70) return null; // niet de hele lange alinea cursief
  return [text.slice(0, start), text.slice(start)];
};

// Analyseer een brieftekst: datumkop weg, adresregels eruit, body + slotgroet-run bepalen.
const analyzeLetter = (text) => {
  let paras = stripMarkers(text).split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
  let flat = paras.map((p) => p.replace(/\n/g, " ").trim());
  // datum/plaats-kopjes en losse initialen bovenaan weghalen ("B. 27-1-'54", "1.", enz.)
  while (paras.length > 1 && (looksLikeDateline(flat[0]) || /^[A-Za-z0-9]\.?$/.test(flat[0]))) {
    paras = paras.slice(1); flat = flat.slice(1);
  }
  // adresblok: honorific-anker + vervolgregels (aan het begin) of anker t/m einde (aan het eind)
  let anchor = -1;
  for (let i = 0; i < paras.length; i++) {
    if (ADDRESS_RE.test(paras[i].split("\n")[0].trim())) { anchor = i; break; }
  }
  const addrSet = new Set();
  if (anchor !== -1) {
    if (anchor === 0) {
      addrSet.add(0);
      for (let i = 1; i < paras.length; i++) {
        if (flat[i].length <= 40 && !SALUTATION_RE.test(flat[i])) addrSet.add(i); else break;
      }
    } else {
      for (let i = anchor; i < paras.length; i++) addrSet.add(i);
    }
  }
  // losse afzender-/adresregel (bijv. "I. Koning Corn. Krusemanstr. 16 II Amsd.") —
  // herkend aan straatnaam + huisnummer, zowel aan het begin als aan het eind.
  const STREET_RE = /(straat|str\.?|laan|weg|plein|kade|gracht|singel|dijk|hof)(?![a-z])/i;
  const isStreetAddr = (line) => line.length <= 70 && STREET_RE.test(line) && /\d/.test(line);
  if (!addrSet.has(0)) {
    for (let i = 0; i < paras.length; i++) {
      if (isStreetAddr(flat[i])) addrSet.add(i); else break;
    }
  }
  for (let i = paras.length - 1; i >= 0; i--) {
    if (addrSet.has(i)) continue;
    if (isStreetAddr(flat[i])) addrSet.add(i); else break;
  }
  const addrLines = [...addrSet].sort((a, b) => a - b).flatMap((i) => paras[i].split(/\n|·|,/).map((s) => s.trim()).filter(Boolean));
  const body = flat.filter((_, i) => !addrSet.has(i));
  const salut = body.length > 0 && SALUTATION_RE.test(body[0]) && body[0].length <= 60;
  const dropcapIdx = salut ? 1 : 0;
  // slotgroet herkennen vanaf het eind; P.S./N.B. overslaan (die horen onderaan maar zijn geen groet)
  const PS_RE = /^(p\.?\s*s\.?|n\.?\s*b\.?)\b/i;
  const closingSet = new Set();
  const psSet = new Set();
  let closingSplitIdx = -1;
  for (let i = body.length - 1; i > dropcapIdx; i--) {
    const t = body[i];
    if (PS_RE.test(t)) { psSet.add(i); continue; }
    if ((CLOSING_CUE.test(t) || t.length <= 40) && t.length <= 120) { closingSet.add(i); continue; }
    closingSplitIdx = i; break;
  }
  if (closingSplitIdx === -1) {
    for (let i = body.length - 1; i >= dropcapIdx; i--) {
      if (!closingSet.has(i) && !psSet.has(i)) { closingSplitIdx = i; break; }
    }
  }
  return { body, addrLines, salut, dropcapIdx, closingSplitIdx, closingSet, psSet };
};

const DROPCAP_CLASS = "first-letter:float-left first-letter:mr-2 first-letter:mt-1 first-letter:font-serif first-letter:text-5xl first-letter:font-bold first-letter:leading-[0.7] first-letter:text-[var(--brand)]";
const ITALIC_CLASS = "italic text-[var(--brand)]";

function AddressPanel({ lines, testid, className = "" }) {
  return (
    <div className={`postcard-address rounded-md border border-[var(--border-subtle)] bg-[var(--surface-muted)]/50 px-4 py-3 ${className}`} data-testid={testid}>
      <div className="mb-3 flex items-center justify-between">
        <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-[var(--ink-3)]">Adres</span>
        <span className="inline-block h-6 w-8 rounded-[2px] border border-dashed border-[var(--ink-3)]/50" aria-hidden="true" />
      </div>
      {lines.map((line, i) => (
        <div key={i} className="border-b border-[var(--ink-3)]/30 py-1.5 font-serif text-[15px] italic leading-snug text-[var(--ink-2)]">{line}</div>
      ))}
    </div>
  );
}

function BodyParagraph({ text, index, dropcapIdx, closingSplitIdx, salut, closingSet, psSet, q, quoteState }) {
  if ((salut && index === 0) || (closingSet && closingSet.has(index))) {
    return <p className={`font-serif text-[17px] leading-[1.9] ${ITALIC_CLASS} ${index > 0 ? "mt-4" : ""}`}>{text}</p>;
  }
  const isPs = psSet && psSet.has(index);
  let sal = null, close = null, mid = text;
  if (index === dropcapIdx && !isPs) { const s = splitSalutation(mid); if (s) { sal = s[0]; mid = s[1]; } }
  if (index === closingSplitIdx && !isPs) { const c = splitClosing(mid); if (c) { mid = c[0]; close = c[1]; } }
  let midNode = mid;
  if (q && quoteState && !quoteState.found) {
    const res = highlightInParagraph(mid, q);
    if (res.matched) { quoteState.found = true; midNode = res.node; }
  }
  const dropcap = index === dropcapIdx && !sal && !isPs && /^\s*['"(]?[A-Za-zÀ-ÿ]/.test(mid);
  const indent = index > dropcapIdx;
  return (
    <p className={`font-serif text-[17px] leading-[1.9] text-[var(--ink)] ${index > 0 ? "mt-4" : ""} ${indent ? "indent-8" : ""} ${dropcap ? DROPCAP_CLASS : ""}`}>
      {sal && <span className={ITALIC_CLASS}>{sal}</span>}
      {midNode}
      {close && <span className={ITALIC_CLASS}>{close}</span>}
    </p>
  );
}

const FORMATS = {
  a4: { label: "A4", widthPx: 794, size: "A4", margin: "20mm 18mm", pad: 40 },
  a5: { label: "A5", widthPx: 560, size: "A5", margin: "14mm 13mm", pad: 30 },
};

const printCss = (fmt) => `
@media print {
  .no-print { display: none !important; }
  html, body { background: #ffffff !important; }
  .book-page { box-shadow: none !important; margin: 0 !important; border: 0 !important; max-width: none !important; width: auto !important; }
  .book-flip-viewport { width: auto !important; height: auto !important; max-width: none !important; overflow: visible !important; box-shadow: none !important; border: 0 !important; padding: 0 !important; background: #fff !important; }
  .book-flip-content { column-width: auto !important; columns: unset !important; column-gap: normal !important; column-fill: balance !important; transform: none !important; height: auto !important; }
  .chapter { break-before: page; page-break-before: always; }
  .toc { break-before: page; page-break-before: always; }
  .letter-break { break-before: page; page-break-before: always; }
  .book-letter, figure, .obs, .postcard-address, .overview-year, blockquote { break-inside: avoid; page-break-inside: avoid; }
}
@page { size: ${fmt.size}; margin: ${fmt.margin}; }
`;

/* Pagineert de boekinhoud in schermbrede 'pagina's' via CSS-kolommen; omslaan met pijltjes, toetsen of tik. */
const FlipBook = forwardRef(function FlipBook({ pageW, pageH, pad, spread, children }, ref) {
  const viewportRef = useRef(null);
  const contentRef = useRef(null);
  const [index, setIndex] = useState(0);
  const [pages, setPages] = useState(1);
  const viewCount = spread ? 2 : 1;
  const colW = pageW - pad * 2;
  const colGap = pad * 2;
  const step = colW + colGap;
  const innerH = pageH - pad * 2;
  const viewportW = pageW * viewCount;
  const spreads = Math.max(1, Math.ceil(pages / viewCount));

  useImperativeHandle(ref, () => ({
    goToElement(elementId) {
      const root = contentRef.current;
      if (!root) return;
      const el = root.querySelector(`#${CSS.escape(elementId)}`);
      if (!el) return;
      const col = Math.round(el.offsetLeft / step);
      setIndex(Math.max(0, Math.min(spreads - 1, Math.floor(col / viewCount))));
      if (viewportRef.current) viewportRef.current.scrollLeft = 0;
    },
  }));

  const recompute = () => {
    const el = contentRef.current;
    if (!el) return;
    const total = Math.max(1, Math.round((el.scrollWidth + colGap) / step));
    setPages(total);
    const sp = Math.max(1, Math.ceil(total / viewCount));
    setIndex((i) => Math.min(i, sp - 1));
  };

  useEffect(() => {
    const t1 = setTimeout(recompute, 80);
    const t2 = setTimeout(recompute, 400);
    const ro = new ResizeObserver(() => recompute());
    if (contentRef.current) ro.observe(contentRef.current);
    window.addEventListener("resize", recompute);
    return () => { clearTimeout(t1); clearTimeout(t2); ro.disconnect(); window.removeEventListener("resize", recompute); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageW, pageH, pad, spread]);

  const go = (d) => setIndex((i) => Math.max(0, Math.min(spreads - 1, i + d)));
  useEffect(() => {
    const onKey = (e) => {
      if (e.target && /INPUT|TEXTAREA/.test(e.target.tagName)) return;
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spreads]);

  const leftPage = index * viewCount + 1;
  const rightPage = Math.min(index * viewCount + viewCount, pages);
  const indicator = spread && rightPage > leftPage ? `${leftPage}\u2013${rightPage} / ${pages}` : `${leftPage} / ${pages}`;

  const flipStyle = `
.book-flip-content figure, .book-flip-content blockquote, .book-flip-content .obs, .book-flip-content .postcard-address, .book-flip-content .overview-year, .book-flip-content .overview-chapter, .book-flip-content .chapter-head, .book-flip-content header.cover, .book-flip-content nav.toc { break-inside: avoid; }
.book-flip-content .book-letter { break-inside: avoid; }
.book-flip-content img { max-height: ${Math.round(innerH * 0.52)}px; width: auto; object-fit: contain; }
.book-flip-content .book-scan { max-height: ${Math.min(150, Math.round(innerH * 0.28))}px; }
.book-flip-content header.cover .book-cover-img { max-height: ${Math.round(innerH * 0.72)}px; }
.book-flip-content header.cover { break-after: column; }
.book-flip-content nav.toc { break-after: column; }
.book-flip-content section.chapter { break-before: column; }
.book-flip-content section.intro-chapter { break-before: avoid; }
.book-flip-content .letter-break { break-before: column; }
`;

  return (
    <div className="flex flex-col items-center">
      <style>{flipStyle}</style>
      <div
        ref={viewportRef}
        onScroll={(e) => { e.currentTarget.scrollLeft = 0; e.currentTarget.scrollTop = 0; }}
        className="book-flip-viewport relative mx-auto overflow-hidden rounded-[18px] bg-[var(--paper)] shadow-2xl ring-1 ring-black/5"
        style={{ width: viewportW, height: pageH, padding: pad }}
        data-testid="book-flip-viewport"
      >
        <div
          ref={contentRef}
          className="book-flip-content"
          style={{
            columnWidth: `${colW}px`,
            columnGap: `${colGap}px`,
            columnFill: "auto",
            height: innerH,
            transform: `translateX(-${index * step * viewCount}px)`,
            transition: "transform .42s cubic-bezier(.4,0,.2,1)",
            willChange: "transform",
          }}
        >
          {children}
        </div>
        {/* middenvouw/rug bij een twee-pagina-spread */}
        {spread && (
          <>
            <div className="pointer-events-none absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-black/10" />
            <div className="pointer-events-none absolute inset-y-0 left-1/2 w-16 -translate-x-1/2" style={{ background: "linear-gradient(to right, transparent, rgba(0,0,0,0.06), transparent)" }} />
          </>
        )}
        {/* bladzones aan de buitenranden (laten het midden klikbaar) */}
        <button aria-label="Vorige pagina" onClick={() => go(-1)} disabled={index === 0} className="no-print absolute inset-y-0 left-0 w-[14%] cursor-pointer disabled:cursor-default" data-testid="flip-prev-zone" />
        <button aria-label="Volgende pagina" onClick={() => go(1)} disabled={index >= spreads - 1} className="no-print absolute inset-y-0 right-0 w-[14%] cursor-pointer disabled:cursor-default" data-testid="flip-next-zone" />
      </div>

      <div className="no-print mt-5 flex items-center gap-5">
        <button onClick={() => go(-1)} disabled={index === 0} data-testid="flip-prev" className="flex h-11 w-11 items-center justify-center rounded-full border border-[var(--border-subtle)] bg-[var(--surface)] text-[var(--ink-2)] transition-colors hover:bg-[var(--surface-muted)] disabled:opacity-40">
          <ChevronLeft className="h-5 w-5" />
        </button>
        <span data-testid="flip-page-indicator" className="min-w-[88px] text-center font-mono text-sm text-[var(--ink-2)]">
          {indicator}
        </span>
        <button onClick={() => go(1)} disabled={index >= spreads - 1} data-testid="flip-next" className="flex h-11 w-11 items-center justify-center rounded-full border border-[var(--border-subtle)] bg-[var(--surface)] text-[var(--ink-2)] transition-colors hover:bg-[var(--surface-muted)] disabled:opacity-40">
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
      <p className="no-print mt-3 text-center text-xs text-[var(--ink-3)]">Sla om met de pijltjes, de pijltjestoetsen of een tik op de linker-/rechterkant.</p>
    </div>
  );
});

export default function FullBook() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [format, setFormat] = useState("a5");
  const [mode, setMode] = useState("flip"); // "scroll" | "flip"
  const [showQuotes, setShowQuotes] = useState(false);
  const [letters, setLetters] = useState([]);
  const [chapters, setChapters] = useState({});
  const [loading, setLoading] = useState(true);
  const [gen, setGen] = useState({ busy: false, done: 0, total: 0 });
  const [coverUrl, setCoverUrl] = useState("");
  const [savingCover, setSavingCover] = useState(false);
  const coverInputRef = useRef(null);

  // Inleiding
  const [introText, setIntroText] = useState("");
  const [editingIntro, setEditingIntro] = useState(false);
  const [introDraft, setIntroDraft] = useState("");
  const [introBusy, setIntroBusy] = useState(false);

  // Observeringen
  const [observations, setObservations] = useState([]);
  const [obsBusy, setObsBusy] = useState(false);
  const [editingObsId, setEditingObsId] = useState("");
  const [obsDraft, setObsDraft] = useState("");

  // Jaar- & hoofdstukoverzichten
  const [overviews, setOverviews] = useState([]);
  const [ovBusy, setOvBusy] = useState(false);
  const [editingOvId, setEditingOvId] = useState("");
  const [ovDraft, setOvDraft] = useState("");

  // Schermmaat (voor bladermodus)
  const [vp, setVp] = useState({ w: typeof window !== "undefined" ? window.innerWidth : 1200, h: typeof window !== "undefined" ? window.innerHeight : 800 });
  useEffect(() => {
    const on = () => setVp({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);

  const loadAll = async () => {
    try {
      const [ls, ch, cv, intro, obs, ovs] = await Promise.all([
        api.listLetters({ thumbnails: true }),
        api.getBookChapters(),
        api.getBookCover(),
        api.getBookIntro(),
        api.getBookObservations(),
        api.getBookOverviews(),
      ]);
      setLetters(ls || []);
      setChapters(ch || {});
      setCoverUrl(cv?.cover || "");
      setIntroText(intro?.intro || "");
      setObservations(obs || []);
      setOverviews(ovs || []);
    } catch (e) {
      /* toon lege staat */
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    document.title = "De brieven van Pier & Ien — Boek";
    loadAll();
    return () => { document.title = "Pamaverliefd"; };
  }, []);

  const onPickCover = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setSavingCover(true);
    try {
      const dataUrl = await fileToResizedDataUrl(file, 1600, 0.88);
      const res = await api.setBookCover(dataUrl);
      setCoverUrl(res?.cover || "");
      toast.success("Boekomslag bijgewerkt");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Omslag uploaden mislukt");
    } finally {
      setSavingCover(false);
    }
  };

  const resetCover = async () => {
    setSavingCover(true);
    try {
      await api.setBookCover("");
      setCoverUrl("");
      toast.success("Standaardomslag hersteld");
    } catch (err) {
      toast.error("Herstellen mislukt");
    } finally {
      setSavingCover(false);
    }
  };

  const generateIntro = async () => {
    setIntroBusy(true);
    try {
      const res = await api.generateBookIntro();
      setIntroText(res?.intro || "");
      toast.success("Inleiding gegenereerd");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Inleiding genereren mislukt");
    } finally {
      setIntroBusy(false);
    }
  };

  const startEditIntro = () => { setIntroDraft(introText); setEditingIntro(true); };
  const saveIntro = async () => {
    setIntroBusy(true);
    try {
      const res = await api.setBookIntro(introDraft);
      setIntroText(res?.intro || "");
      setEditingIntro(false);
      toast.success("Inleiding opgeslagen");
    } catch (e) {
      toast.error("Opslaan mislukt");
    } finally {
      setIntroBusy(false);
    }
  };

  const generateObservations = async () => {
    setObsBusy(true);
    try {
      const res = await api.generateBookObservations();
      setObservations(res?.observations || []);
      if ((res?.created || 0) > 0) toast.success(`${res.created}x 'Wat opvalt' toegevoegd`);
      else if ((res?.gaps_found || 0) === 0) toast.info("Geen opvallende gaten tussen de brieven gevonden");
      else toast.info("Alle gaten hebben al een 'Wat opvalt'-tekst");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Genereren mislukt");
    } finally {
      setObsBusy(false);
    }
  };

  const startEditObs = (obs) => { setEditingObsId(obs.id); setObsDraft(obs.text); };
  const saveObs = async (id) => {
    try {
      const updated = await api.updateBookObservation(id, obsDraft);
      setObservations((prev) => prev.map((o) => (o.id === id ? updated : o)));
      setEditingObsId("");
      toast.success("'Wat opvalt' opgeslagen");
    } catch (e) {
      toast.error("Opslaan mislukt");
    }
  };
  const removeObs = async (id) => {
    if (!window.confirm("Deze 'Wat opvalt'-tekst verwijderen? Opnieuw genereren kost een nieuwe AI-aanvraag.")) return;
    try {
      await api.deleteBookObservation(id);
      setObservations((prev) => prev.filter((o) => o.id !== id));
      toast.success("'Wat opvalt' verwijderd");
    } catch (e) {
      toast.error("Verwijderen mislukt");
    }
  };

  const generateOverviews = async () => {
    setOvBusy(true);
    try {
      const res = await api.generateBookOverviews();
      setOverviews(res?.overviews || []);
      if ((res?.created || 0) > 0) toast.success(`${res.created} overzicht${res.created === 1 ? "" : "en"} toegevoegd`);
      else toast.info("Alle jaren en hoofdstukken hebben al een overzicht");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Overzichten genereren mislukt");
    } finally {
      setOvBusy(false);
    }
  };
  const startEditOv = (ov) => { setEditingOvId(ov.id); setOvDraft(ov.text); };
  const saveOv = async (id) => {
    try {
      const updated = await api.updateBookOverview(id, ovDraft);
      setOverviews((prev) => prev.map((o) => (o.id === id ? updated : o)));
      setEditingOvId("");
      toast.success("Overzicht opgeslagen");
    } catch (e) {
      toast.error("Opslaan mislukt");
    }
  };
  const removeOv = async (id) => {
    if (!window.confirm("Dit overzicht verwijderen? Opnieuw genereren kost een nieuwe AI-aanvraag.")) return;
    try {
      await api.deleteBookOverview(id);
      setOverviews((prev) => prev.filter((o) => o.id !== id));
      toast.success("Overzicht verwijderd");
    } catch (e) {
      toast.error("Verwijderen mislukt");
    }
  };

  const groups = useMemo(() => {
    const map = {};
    for (const l of letters) {
      const k = ymKey(l);
      (map[k] = map[k] || []).push(l);
    }
    const keys = Object.keys(map).sort();
    const dated = keys.filter((k) => k !== "0000-00");
    const rest = keys.filter((k) => k === "0000-00");
    return [...dated, ...rest].map((k) => ({
      ym: k,
      year: k.slice(0, 4),
      month: Number(k.slice(5, 7)),
      letters: map[k].slice().sort((a, b) => dayKey(a) - dayKey(b)),
    }));
  }, [letters]);

  const obsByAnchor = useMemo(() => {
    const m = {};
    for (const o of observations) m[o.anchor_ym] = o;
    return m;
  }, [observations]);

  const yearOverview = useMemo(() => {
    const m = {};
    for (const o of overviews) if (o.kind === "year") m[o.key] = o;
    return m;
  }, [overviews]);
  const chapterOverview = useMemo(() => {
    const m = {};
    for (const o of overviews) if (o.kind === "chapter") m[o.key] = o;
    return m;
  }, [overviews]);

  const period = useMemo(() => {
    const yrs = letters.map((l) => (l.date_iso || "").match(/^(\d{4})/)).filter(Boolean).map((m) => Number(m[1]));
    if (!yrs.length) return "";
    const lo = Math.min(...yrs), hi = Math.max(...yrs);
    return lo === hi ? `${lo}` : `${lo} – ${hi}`;
  }, [letters]);

  const missingTitles = groups.filter((g) => g.ym !== "0000-00" && !chapters[g.ym]);

  const flipRef = useRef(null);
  const scrollToChapter = (ym) => {
    const elementId = `chapter-${ym}`;
    if (mode === "flip") {
      flipRef.current?.goToElement(elementId);
      return;
    }
    const el = document.getElementById(elementId);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const generateTitles = async () => {
    if (!missingTitles.length) { toast.info("Alle hoofdstukken hebben al een titel"); return; }
    setGen({ busy: true, done: 0, total: missingTitles.length });
    try {
      const titles = await api.generateChapterTitles(missingTitles.map((g) => ({ year: g.year, month: String(g.month) })));
      setChapters((prev) => ({ ...prev, ...titles }));
      toast.success("Hoofdstuktitels gegenereerd");
    } catch (e) {
      toast.error("Titels genereren mislukt");
    } finally {
      setGen({ busy: false, done: 0, total: 0 });
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--paper)] text-[var(--ink-3)]">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  const fmt = FORMATS[format];
  const introParas = paragraphs(introText);
  const canEdit = isAdmin;
  const usingDefaultCover = !coverUrl;

  const spread = vp.w >= fmt.widthPx * 2 + 64;
  const pageW = spread ? fmt.widthPx : Math.min(fmt.widthPx, vp.w - 24);
  const pageH = Math.min(vp.h - 190, Math.round(pageW * 1.414));
  const obsLen = observations.reduce((s, o) => s + (o.text || "").length, 0);
  const ovLen = overviews.reduce((s, o) => s + (o.text || "").length, 0);
  const flipKey = `${format}-${letters.length}-${observations.length}-${obsLen}-${overviews.length}-${ovLen}-${introText.length}-${showQuotes ? 1 : 0}-${Object.keys(chapters).length}-${pageW}x${pageH}-${spread ? "s" : "1"}`;

  const ObservationBlock = ({ obs }) => (
    <aside className="obs mx-auto my-12 max-w-[520px]" data-testid={`book-observation-${obs.anchor_ym}`}>
      <div className="relative rounded-xl border border-[var(--brand)]/25 px-7 py-6 shadow-sm" style={{ backgroundColor: "color-mix(in srgb, var(--brand) 6%, var(--paper))" }}>
        <div className="mb-3 flex items-center justify-center gap-2 text-[var(--brand)]">
          <NotebookPen className="h-4 w-4" />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Wat opvalt</span>
        </div>
        {editingObsId === obs.id ? (
          <div className="no-print">
            <textarea value={obsDraft} onChange={(e) => setObsDraft(e.target.value)} rows={6} data-testid={`obs-edit-textarea-${obs.anchor_ym}`} className="w-full rounded-lg border border-[var(--border-subtle)] bg-[var(--surface)] p-3 font-serif text-[15px] leading-relaxed text-[var(--ink)] focus:border-[var(--brand)] focus:outline-none" />
            <div className="mt-2 flex justify-end gap-2">
              <button onClick={() => setEditingObsId("")} className="flex items-center gap-1 rounded-lg border border-[var(--border-subtle)] px-3 py-1.5 text-xs font-semibold text-[var(--ink-2)]"><X className="h-3.5 w-3.5" /> Annuleren</button>
              <button onClick={() => saveObs(obs.id)} data-testid={`obs-save-${obs.anchor_ym}`} className="flex items-center gap-1 rounded-lg bg-[var(--brand)] px-3 py-1.5 text-xs font-semibold text-white"><Check className="h-3.5 w-3.5" /> Opslaan</button>
            </div>
          </div>
        ) : (
          <>
            {(obs.text || "").split(/\n{2,}/).map((p, i) => (
              <p key={i} className={`font-serif text-[16px] italic leading-[1.8] text-[var(--ink-2)] ${i > 0 ? "mt-3" : ""}`}>{p}</p>
            ))}
            <p className="mt-4 text-right font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--brand)]">— {obs.by || "Rogier Rijnja"}</p>
          </>
        )}
        {canEdit && editingObsId !== obs.id && (
          <div className="no-print mt-3 flex justify-end gap-2">
            <button onClick={() => startEditObs(obs)} data-testid={`obs-edit-${obs.anchor_ym}`} className="flex items-center gap-1 rounded-lg border border-[var(--border-subtle)] px-2.5 py-1 text-xs font-medium text-[var(--ink-2)] transition-colors hover:bg-[var(--surface-muted)]"><Pencil className="h-3 w-3" /> Bewerk</button>
            <button onClick={() => removeObs(obs.id)} data-testid={`obs-delete-${obs.anchor_ym}`} className="flex items-center gap-1 rounded-lg border border-red-200 px-2.5 py-1 text-xs font-medium text-red-500 transition-colors hover:bg-red-50"><Trash2 className="h-3 w-3" /> Verwijder</button>
          </div>
        )}
      </div>
    </aside>
  );

  const OverviewBlock = ({ ov, variant }) => {
    const editing = editingOvId === ov.id;
    const paras = (ov.text || "").split(/\n{2,}/);
    const editor = (
      <div className="no-print mx-auto max-w-[560px]">
        <textarea value={ovDraft} onChange={(e) => setOvDraft(e.target.value)} rows={variant === "year" ? 6 : 3} data-testid={`ov-edit-textarea-${ov.key}`} className="w-full rounded-lg border border-[var(--border-subtle)] bg-[var(--surface)] p-3 font-serif text-[15px] leading-relaxed text-[var(--ink)] focus:border-[var(--brand)] focus:outline-none" />
        <div className="mt-2 flex justify-end gap-2">
          <button onClick={() => setEditingOvId("")} className="flex items-center gap-1 rounded-lg border border-[var(--border-subtle)] px-3 py-1.5 text-xs font-semibold text-[var(--ink-2)]"><X className="h-3.5 w-3.5" /> Annuleren</button>
          <button onClick={() => saveOv(ov.id)} data-testid={`ov-save-${ov.key}`} className="flex items-center gap-1 rounded-lg bg-[var(--brand)] px-3 py-1.5 text-xs font-semibold text-white"><Check className="h-3.5 w-3.5" /> Opslaan</button>
        </div>
      </div>
    );
    const adminBtns = canEdit && !editing && (
      <div className="no-print mt-3 flex justify-center gap-2">
        <button onClick={() => startEditOv(ov)} data-testid={`ov-edit-${ov.key}`} className="flex items-center gap-1 rounded-lg border border-[var(--border-subtle)] px-2.5 py-1 text-xs font-medium text-[var(--ink-2)] transition-colors hover:bg-[var(--surface-muted)]"><Pencil className="h-3 w-3" /> Bewerk</button>
        <button onClick={() => removeOv(ov.id)} data-testid={`ov-delete-${ov.key}`} className="flex items-center gap-1 rounded-lg border border-red-200 px-2.5 py-1 text-xs font-medium text-red-500 transition-colors hover:bg-red-50"><Trash2 className="h-3 w-3" /> Verwijder</button>
      </div>
    );
    if (variant === "year") {
      return (
        <section className="overview-year mb-12" data-testid={`book-overview-${ov.key}`}>
          <div className="mx-auto max-w-[600px] rounded-xl border border-[var(--brand)]/25 px-8 py-7 text-center" style={{ backgroundColor: "color-mix(in srgb, var(--brand) 5%, var(--paper))" }}>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[var(--brand)]">Jaaroverzicht</p>
            <h2 className="mt-1 font-serif text-4xl font-bold text-[var(--ink)]">{ov.label}</h2>
            <div className="mx-auto my-4 h-px w-16 bg-[var(--brand)]/40" />
            {editing ? editor : paras.map((p, i) => (<p key={i} className={`font-serif text-[16px] italic leading-[1.8] text-[var(--ink-2)] ${i > 0 ? "mt-3" : ""}`}>{p}</p>))}
            {adminBtns}
          </div>
        </section>
      );
    }
    return (
      <div className="overview-chapter mx-auto mb-8 max-w-[520px] text-center" data-testid={`book-overview-${ov.key}`}>
        {editing ? editor : paras.map((p, i) => (<p key={i} className={`font-serif text-[15px] italic leading-[1.7] text-[var(--ink-3)] ${i > 0 ? "mt-2" : ""}`}>{p}</p>))}
        {adminBtns}
      </div>
    );
  };
  const bookInner = (
    <>
      {/* Omslag */}
      <header className="cover mb-16 text-center">
        {usingDefaultCover ? (
          <>
            <figure className="relative mx-auto inline-block overflow-hidden rounded-xl border border-[var(--border-subtle)] shadow-md">
              <img src={COVER_PHOTO} alt="Boekomslag: Pieter & Ineke — Brieven en kaarten" className="book-cover-img mx-auto max-h-[58vh] w-auto object-contain" data-testid="book-cover-photo" />
              {canEdit && (
                <div className="no-print absolute bottom-3 right-3 flex gap-2">
                  <input ref={coverInputRef} type="file" accept="image/*" onChange={onPickCover} className="hidden" data-testid="book-cover-input" />
                  <button onClick={() => coverInputRef.current?.click()} disabled={savingCover} data-testid="book-cover-change" className="flex items-center gap-1.5 rounded-lg bg-black/55 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur transition-colors hover:bg-black/70 disabled:opacity-60">
                    {savingCover ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />}
                    Eigen foto
                  </button>
                </div>
              )}
            </figure>
            {period && <p className="mt-6 font-serif text-lg italic text-[var(--ink-2)]">{period}</p>}
            <p className="mt-2 font-mono text-xs text-[var(--ink-3)]">{letters.length} {letters.length === 1 ? "brief" : "brieven"} · {groups.length} {groups.length === 1 ? "hoofdstuk" : "hoofdstukken"}</p>
          </>
        ) : (
          <>
            <figure className="relative mb-8 overflow-hidden rounded-xl border border-[var(--border-subtle)] shadow-sm">
              <img src={coverUrl} alt="Omslagfoto van het boek" className="h-52 w-full object-cover sm:h-72" data-testid="book-cover-photo" />
              {canEdit && (
                <div className="no-print absolute bottom-3 right-3 flex gap-2">
                  <input ref={coverInputRef} type="file" accept="image/*" onChange={onPickCover} className="hidden" data-testid="book-cover-input" />
                  <button onClick={() => coverInputRef.current?.click()} disabled={savingCover} data-testid="book-cover-change" className="flex items-center gap-1.5 rounded-lg bg-black/55 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur transition-colors hover:bg-black/70 disabled:opacity-60">
                    {savingCover ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />}
                    Omslag wijzigen
                  </button>
                  <button onClick={resetCover} disabled={savingCover} data-testid="book-cover-reset" className="rounded-lg bg-black/55 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur transition-colors hover:bg-black/70 disabled:opacity-60">Standaardomslag</button>
                </div>
              )}
            </figure>
            <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-[var(--ink-3)]">De verzamelde brieven</p>
            <h1 className="mt-4 font-serif text-4xl font-bold leading-tight text-[var(--ink)] sm:text-5xl">Pieter &amp; Ineke</h1>
            <p className="mt-5 font-serif text-xl italic text-[var(--ink-2)]">Brieven en kaarten{period ? `, ${period}` : ""}</p>
            <p className="mt-2 font-mono text-xs text-[var(--ink-3)]">{letters.length} {letters.length === 1 ? "brief" : "brieven"} · {groups.length} {groups.length === 1 ? "hoofdstuk" : "hoofdstukken"}</p>
          </>
        )}
      </header>

      {/* Inhoudsopgave */}
      {groups.length > 0 && (
        <nav className="toc chapter mb-14" data-testid="book-toc">
          <div className="mb-8 text-center">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[var(--brand)]">Inhoudsopgave</p>
            <div className="mx-auto mt-4 h-px w-16 bg-[var(--brand)]/40" />
          </div>
          <ol className="mx-auto max-w-[560px] space-y-3">
            {introText && (
              <li>
                <button type="button" onClick={() => scrollToChapter("intro")} data-testid="toc-item-intro" className="group flex w-full items-baseline gap-2 text-left">
                  <span className="w-6 shrink-0 font-mono text-sm text-[var(--brand)]">—</span>
                  <span className="min-w-0 font-serif text-[17px] text-[var(--ink)] transition-colors group-hover:text-[var(--brand)]">Hoe het allemaal begon</span>
                  <span className="mx-1 min-w-[1.5rem] flex-1 translate-y-[-3px] border-b border-dotted border-[var(--ink-3)]/40" />
                  <span className="w-32 shrink-0 whitespace-nowrap text-left font-mono text-[11px] uppercase tracking-wider text-[var(--ink-3)]">Inleiding</span>
                </button>
              </li>
            )}
            {groups.map((g, gi) => {
              const title = chapters[g.ym] || "";
              const monthLabel = g.ym === "0000-00" ? "Zonder datum" : `${MONTHS_NL[g.month]} ${g.year}`;
              return (
                <li key={g.ym}>
                  <button type="button" onClick={() => scrollToChapter(g.ym)} data-testid={`toc-item-${g.ym}`} className="group flex w-full items-baseline gap-2 text-left">
                    <span className="w-6 shrink-0 font-mono text-sm text-[var(--brand)]">{String(gi + 1).padStart(2, "0")}</span>
                    <span className="min-w-0 font-serif text-[17px] text-[var(--ink)] transition-colors group-hover:text-[var(--brand)]">{title || monthLabel}</span>
                    <span className="mx-1 min-w-[1.5rem] flex-1 translate-y-[-3px] border-b border-dotted border-[var(--ink-3)]/40" />
                    <span className="w-32 shrink-0 whitespace-nowrap text-left font-mono text-[11px] uppercase tracking-wider text-[var(--ink-3)]">{monthLabel}</span>
                  </button>
                </li>
              );
            })}
          </ol>
        </nav>
      )}

      {/* Inleiding — Hoe het allemaal begon */}
      {introText ? (
        <section id="chapter-intro" className="chapter intro-chapter mb-14" data-testid="book-intro">
          <div className="chapter-head mb-8 text-center">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[var(--brand)]">Ter inleiding</p>
            <h2 className="mt-2 font-serif text-3xl font-bold leading-tight text-[var(--ink)]">Hoe het allemaal begon</h2>
            <div className="mx-auto mt-4 h-px w-16 bg-[var(--brand)]/40" />
          </div>
          {editingIntro ? (
            <div className="no-print mx-auto max-w-[560px]">
              <textarea value={introDraft} onChange={(e) => setIntroDraft(e.target.value)} rows={12} data-testid="intro-edit-textarea" className="w-full rounded-lg border border-[var(--border-subtle)] bg-[var(--surface)] p-4 font-serif text-[16px] leading-relaxed text-[var(--ink)] focus:border-[var(--brand)] focus:outline-none" />
              <div className="mt-2 flex justify-end gap-2">
                <button onClick={() => setEditingIntro(false)} className="flex items-center gap-1 rounded-lg border border-[var(--border-subtle)] px-3 py-1.5 text-sm font-semibold text-[var(--ink-2)]"><X className="h-4 w-4" /> Annuleren</button>
                <button onClick={saveIntro} disabled={introBusy} data-testid="intro-save" className="flex items-center gap-1 rounded-lg bg-[var(--brand)] px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-60">{introBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Opslaan</button>
              </div>
            </div>
          ) : (
            <div className="mx-auto max-w-[560px]">
              {introParas.map((p, i) => (
                <p key={i} className={`font-serif text-[17px] leading-[1.9] text-[var(--ink)] ${i > 0 ? "mt-4 indent-8" : ""}`}>{p}</p>
              ))}
              {canEdit && (
                <div className="no-print mt-4 flex justify-center gap-2">
                  <button onClick={startEditIntro} data-testid="intro-edit" className="flex items-center gap-1 rounded-lg border border-[var(--border-subtle)] px-3 py-1.5 text-xs font-medium text-[var(--ink-2)] transition-colors hover:bg-[var(--surface-muted)]"><Pencil className="h-3.5 w-3.5" /> Inleiding bewerken</button>
                  <button onClick={generateIntro} disabled={introBusy} data-testid="intro-regenerate" className="flex items-center gap-1 rounded-lg border border-[var(--brand)]/40 px-3 py-1.5 text-xs font-semibold text-[var(--brand)] transition-colors hover:bg-[var(--brand)]/10 disabled:opacity-60">{introBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Opnieuw genereren</button>
                </div>
              )}
            </div>
          )}
        </section>
      ) : canEdit ? (
        <section className="chapter intro-chapter mb-14" data-testid="book-intro-empty">
          <div className="mx-auto max-w-[520px] rounded-xl border border-dashed border-[var(--border-subtle)] bg-[var(--surface-muted)] px-6 py-10 text-center">
            <BookOpen className="mx-auto mb-3 h-6 w-6 text-[var(--brand)]" />
            <p className="font-serif text-[17px] text-[var(--ink)]">Voeg een warme inleiding toe</p>
            <p className="mx-auto mt-2 max-w-[400px] text-sm text-[var(--ink-3)]">Een korte opening “Hoe het allemaal begon” over hoe Pieter en Ineke elkaar leerden kennen bij het Apostolisch Genootschap aan de Marnixstraat.</p>
            <button onClick={generateIntro} disabled={introBusy} data-testid="intro-generate" className="mx-auto mt-5 flex items-center gap-2 rounded-lg bg-[var(--brand)] px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60">
              {introBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Genereer inleiding
            </button>
          </div>
        </section>
      ) : null}

      {groups.length === 0 && (
        <p className="py-16 text-center text-[var(--ink-3)]">Er zijn nog geen brieven om in het boek te tonen.</p>
      )}

      {groups.map((g, gi) => {
        const title = chapters[g.ym] || "";
        const monthLabel = g.ym === "0000-00" ? "Zonder datum" : `${MONTHS_NL[g.month]} ${g.year}`;
        const obs = obsByAnchor[g.ym];
        const yov = g.year !== "0000" && (gi === 0 || groups[gi - 1].year !== g.year) ? yearOverview[g.year] : null;
        const cov = chapterOverview[g.ym];
        return (
          <div key={g.ym}>
            {yov && OverviewBlock({ ov: yov, variant: "year" })}
            {obs && ObservationBlock({ obs })}
            <section id={`chapter-${g.ym}`} className="chapter mb-14" data-testid={`book-chapter-${g.ym}`}>
              <div className="chapter-head mb-8 text-center">
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[var(--brand)]">{monthLabel}</p>
                <h2 className="mt-2 font-serif text-3xl font-bold leading-tight text-[var(--ink)]">{title || monthLabel}</h2>
                <div className="mx-auto mt-4 h-px w-16 bg-[var(--brand)]/40" />
              </div>
              {cov && OverviewBlock({ ov: cov, variant: "chapter" })}

              {g.letters.map((l, li) => {
                const { body, addrLines, salut, closingSplitIdx, dropcapIdx, closingSet, psSet } = analyzeLetter(l.transcription);
                const img = l.thumbnail || l.front_image || (l.images || [])[0] || "";
                const isCard = (l.doc_type || "") === "ansichtkaart";
                const hasAddr = isCard && addrLines.length > 0;
                const q = showQuotes ? (l.pull_quote || "").trim() : "";
                const quoteState = { found: false };
                const scanFigure = (maxH) => (
                  <figure className="shrink-0" data-testid={`book-scan-${l.id}`}>
                    <div className="rounded-md border border-[var(--border-subtle)] bg-[var(--paper)] p-1.5 shadow-md" style={{ transform: "rotate(-1.1deg)" }}>
                      <img src={img} alt="ingescande kaart of brief" className={`book-scan mx-auto ${maxH} w-auto rounded-sm object-contain`} />
                    </div>
                  </figure>
                );
                return (
                  <div key={l.id} className={`book-letter mb-10 ${li > 0 ? "letter-break" : ""}`} data-testid={`book-letter-${l.id}`}>
                    <p className="mb-3 text-center font-mono text-[11px] uppercase tracking-wider text-[var(--ink-3)]">
                      {[l.date_text || l.date_iso, l.place, l.writer].filter(Boolean).join("  ·  ")}
                    </p>

                    {img && hasAddr ? (
                      <div className="my-5 flex flex-wrap items-center justify-center gap-4">
                        {scanFigure("max-h-[120px]")}
                        <AddressPanel lines={addrLines} testid={`book-address-${l.id}`} className="w-[205px] max-w-full" />
                      </div>
                    ) : img ? (
                      <div className="my-5 flex justify-center">{scanFigure("max-h-[185px]")}</div>
                    ) : null}

                    {body.length ? body.map((text, i) => (
                      <BodyParagraph key={i} text={text} index={i} dropcapIdx={dropcapIdx} closingSplitIdx={closingSplitIdx} salut={salut} closingSet={closingSet} psSet={psSet} q={q} quoteState={quoteState} />
                    )) : (
                      <p className="font-serif text-[15px] italic text-[var(--ink-3)]">(nog geen transcriptie)</p>
                    )}

                    {!img && hasAddr && (
                      <AddressPanel lines={addrLines} testid={`book-address-${l.id}`} className="my-6 ml-auto w-full max-w-[300px]" />
                    )}

                    {q && !quoteState.found && (
                      <blockquote data-testid={`book-quote-${l.id}`} className="my-7 border-y border-[var(--brand)]/30 py-5 text-center">
                        <p className="font-serif text-[22px] italic leading-snug text-[var(--ink)]">“{l.pull_quote}”</p>
                        <cite className="mt-3 block font-mono text-[11px] not-italic uppercase tracking-[0.2em] text-[var(--brand)]">— {l.pull_quote_by || l.writer}</cite>
                      </blockquote>
                    )}
                  </div>
                );
              })}
            </section>
          </div>
        );
      })}

      <footer className="mt-10 border-t border-[var(--border-subtle)] pt-5 text-center">
        <p className="font-serif text-sm italic text-[var(--ink-3)]">Samengesteld met Pamaverliefd — bewaard voor de familie.</p>
      </footer>
    </>
  );

  return (
    <div className="min-h-screen bg-[var(--surface-muted)] py-8 print:bg-white print:py-0">
      <style>{printCss(fmt)}</style>

      <div className="no-print mx-auto mb-6 flex max-w-[900px] flex-wrap items-center justify-center gap-3 px-4">
        <div className="flex flex-wrap items-center justify-center gap-2">
          <div className="flex items-center rounded-lg border border-[var(--border-subtle)] bg-[var(--surface)] p-0.5" data-testid="book-mode-toggle">
            <button onClick={() => setMode("scroll")} data-testid="book-mode-scroll" className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${mode === "scroll" ? "bg-[var(--brand)] text-white" : "text-[var(--ink-2)] hover:bg-[var(--surface-muted)]"}`}>
              <ScrollText className="h-4 w-4" /> Doorlopend
            </button>
            <button onClick={() => setMode("flip")} data-testid="book-mode-flip" className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${mode === "flip" ? "bg-[var(--brand)] text-white" : "text-[var(--ink-2)] hover:bg-[var(--surface-muted)]"}`}>
              <BookOpen className="h-4 w-4" /> Bladeren
            </button>
          </div>
          <div className="flex items-center rounded-lg border border-[var(--border-subtle)] bg-[var(--surface)] p-0.5" data-testid="book-format-toggle">
            {Object.entries(FORMATS).map(([key, f]) => (
              <button key={key} onClick={() => setFormat(key)} data-testid={`book-format-${key}`} className={`rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${format === key ? "bg-[var(--brand)] text-white" : "text-[var(--ink-2)] hover:bg-[var(--surface-muted)]"}`}>
                {f.label}
              </button>
            ))}
          </div>
          {isAdmin && (
            <button onClick={() => setShowQuotes((v) => !v)} data-testid="book-toggle-quotes" aria-pressed={showQuotes} className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors ${showQuotes ? "border-[var(--brand)] bg-[var(--brand)]/10 text-[var(--brand)]" : "border-[var(--border-subtle)] text-[var(--ink-2)] hover:bg-[var(--surface-muted)]"}`}>
              <Quote className="h-4 w-4" /> Quotes
            </button>
          )}
          {isAdmin && (
            <button onClick={generateObservations} disabled={obsBusy} data-testid="book-generate-observations" className="flex items-center gap-2 rounded-lg border border-[var(--brand)]/40 bg-[var(--paper)] px-3 py-2 text-sm font-semibold text-[var(--brand)] transition-colors hover:bg-[var(--brand)]/10 disabled:opacity-60">
              {obsBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <NotebookPen className="h-4 w-4" />}
              {obsBusy ? "Wat opvalt…" : "Wat opvalt"}
            </button>
          )}
          {isAdmin && (
            <button onClick={generateOverviews} disabled={ovBusy} data-testid="book-generate-overviews" className="flex items-center gap-2 rounded-lg border border-[var(--brand)]/40 bg-[var(--paper)] px-3 py-2 text-sm font-semibold text-[var(--brand)] transition-colors hover:bg-[var(--brand)]/10 disabled:opacity-60">
              {ovBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {ovBusy ? "Overzichten…" : "Overzichten"}
            </button>
          )}
          {isAdmin && missingTitles.length > 0 && (
            <button onClick={generateTitles} disabled={gen.busy} data-testid="book-generate-titles" className="flex items-center gap-2 rounded-lg border border-[var(--brand)]/40 bg-[var(--paper)] px-3 py-2 text-sm font-semibold text-[var(--brand)] transition-colors hover:bg-[var(--brand)]/10 disabled:opacity-60">
              {gen.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {gen.busy ? "Titels genereren…" : `Genereer ${missingTitles.length} hoofdstuktitel${missingTitles.length === 1 ? "" : "s"}`}
            </button>
          )}
          <button onClick={() => window.print()} data-testid="book-print" className="flex items-center gap-2 rounded-lg bg-[var(--brand)] px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90">
            <Printer className="h-4 w-4" /> Download als PDF
          </button>
        </div>
      </div>

      <p className="no-print mx-auto mb-6 max-w-[840px] px-4 text-center text-xs leading-relaxed text-[var(--ink-3)]" data-testid="book-ereader-hint">
        Kies bij het afdrukken <span className="font-semibold text-[var(--ink-2)]">“Opslaan als pdf”</span>. Dat pdf-bestand kun je op je e-reader (Kobo, Kindle, tablet) zetten en overal als een echt boek lezen.
      </p>

      {mode === "flip" ? (
        <FlipBook ref={flipRef} key={flipKey} pageW={pageW} pageH={pageH} pad={fmt.pad} spread={spread}>
          {bookInner}
        </FlipBook>
      ) : (
        <article data-testid="full-book" style={{ maxWidth: fmt.widthPx }} className="book-page mx-auto w-full rounded-2xl bg-[var(--paper)] px-8 py-14 shadow-sm transition-[max-width] duration-300 print:rounded-none print:px-0 print:py-0 sm:px-12">
          {bookInner}
        </article>
      )}
      <ScrollToTop />
    </div>
  );
}
